import React from "react"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {Body, H2} from "@irati/ui/src/Typography"
import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import {useBudgetsPermissions, useBudgetsList, Budgets} from "@irati/wallet/hooks/budget"
import {ValidationButton} from "@irati/ui/src/FormItem"
import {Hr} from "@irati/ui/src/Layout"
import {useBudgetManagement} from "./hooks"
import {useBudgetModals} from "./Modals"
import {FormErrors} from "./shared/FormErrors"
import useAuth from "@irati/auth/helpers/auth/useAuth"

export default function Heading() {
  const {isBudgetAdmin} = useBudgetsPermissions()
  const {data} = useBudgetsList()
  const {dispatch, state, validate} = useBudgetManagement()
  const {showConfirm, showCancel} = useBudgetModals()
  const {submitting, dirty} = state

  return (
    <Flex gap={2} direction="column">
      <Flex>
        <H2>
          <FormattedMessage id="wallet.reward.budget.dashboard.title" />
        </H2>
        <Flex alignItems="flex-start" justifyContent="flex-end" flex="1" gap={2}>
          {state.editing ? (
            <>
              <ValidationButton
                small
                apolloState={{loading: submitting}}
                disabled={!dirty}
                primary
                type="submit"
                onClick={() => validate(showConfirm)}>
                <FormattedMessage id="platform.button.save" />
              </ValidationButton>
              <Button small onClick={() => showCancel()}>
                <FormattedMessage id="platform.button.cancel" />
              </Button>
            </>
          ) : (
            <Button small primary onClick={() => dispatch({type: "startEdit"})}>
              {isBudgetAdmin ? (
                <FormattedMessage id="wallet.reward.budget.dashboard.cta.edit.admin" />
              ) : (
                <FormattedMessage id="wallet.reward.budget.dashboard.cta.edit.manager" />
              )}
            </Button>
          )}

          <ButtonRouterLink id="rewardWallet" to="/flexi-reward" small>
            <FormattedMessage id="wallet.reward.budget.dashboard.cta.toWallet" />
          </ButtonRouterLink>
        </Flex>
      </Flex>
      {data && <SubHeading {...data} />}
      <FormErrors />
      <Hr flush />
    </Flex>
  )
}

const SubHeading = injectIntl<Budgets>(({total, budgets, intl: {formatNumber}}) => {
  const {
    unassigned: {amount: unassignedPoints}
  } = budgets

  const amount = formatNumber(unassignedPoints / 100)

  if (total === 0) return <SubHeadingZeroBalance />

  if (unassignedPoints !== 0) return <SubHeadingUnassigned amount={amount} />

  return <SubHeadingAllAssigned />
})

function SubHeadingUnassigned({amount}: {amount: string}) {
  return (
    <Body>
      <FormattedMessage id="wallet.reward.budget.dashboard.subHeader.unassignedPoints" values={{amount}} />
    </Body>
  )
}
function SubHeadingZeroBalance() {
  const auth = useAuth()
  const canTopUpRewardPoints = auth.productPermissions?.wallet?.flexiRewards?.canTopUpRewardPoints
  if (canTopUpRewardPoints) {
    return null
  }
  return (
    <Body>
      <FormattedMessage
        id="wallet.reward.budget.dashboard.subHeader.emptyWallet"
        values={{
          cta: (
            <ButtonRouterLink
              id="rewardWalletTopUpButton"
              to="/flexi-checkout/top-up"
              cta
              onClick={() =>
                tracking.buyPoints({
                  source: "rp_budget_dashboard_header"
                })
              }>
              <FormattedMessage id="wallet.reward.budget.dashboard.cta.topUp" />
            </ButtonRouterLink>
          )
        }}
      />
    </Body>
  )
}

function SubHeadingAllAssigned() {
  const auth = useAuth()
  const canTopUpRewardPoints = auth.productPermissions?.wallet?.flexiRewards?.canTopUpRewardPoints
  if (canTopUpRewardPoints) {
    return null
  }
  return (
    <Body>
      <FormattedMessage
        id="wallet.reward.budget.dashboard.subHeader.allAssigned"
        values={{
          cta: (
            <ButtonRouterLink
              id="rewardWalletTopUpButton"
              to="/flexi-checkout/top-up"
              cta
              onClick={() =>
                tracking.buyPoints({
                  source: "rp_budget_dashboard_header"
                })
              }>
              <FormattedMessage id="wallet.reward.budget.dashboard.cta.topUp" />
            </ButtonRouterLink>
          )
        }}
      />
    </Body>
  )
}
